"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { WorkerCommand } from "@/lib/navigation/simulator/types";

export function useSimulatorWorker() {
    const worker = useRef<Worker | null>(null);
    const counter = useRef(0);
    const pending = useRef(new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>());
    const [ready, setReady] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const requests = pending.current;
        let instance: Worker;
        try {
            instance = new Worker(new URL("../../../lib/navigation/simulator/worker.ts", import.meta.url));
        } catch {
            setError("Браузер не зміг запустити обчислювальний модуль симулятора."); return;
        }
        worker.current = instance;
        instance.onmessage = ({ data }: MessageEvent<{ requestId: number; ok: boolean; result?: unknown; error?: string }>) => {
            const request = requests.get(data.requestId);
            if (!request) return;
            clearTimeout(request.timer); requests.delete(data.requestId);
            if (data.ok) request.resolve(data.result);
            else request.reject(new Error(data.error ?? "Помилка обчислення."));
        };
        instance.onerror = () => {
            setError("Обчислювальний модуль зупинився. Оновіть сторінку або спробуйте менший набір даних.");
            setReady(false);
            for (const request of requests.values()) { clearTimeout(request.timer); request.reject(new Error("Worker недоступний.")); }
            requests.clear();
        };
        setReady(true);
        return () => {
            instance.terminate(); worker.current = null;
            for (const request of requests.values()) { clearTimeout(request.timer); request.reject(new Error("Симулятор закрито.")); }
            requests.clear();
        };
    }, []);

    const call = useCallback(<T,>(command: WorkerCommand): Promise<T> => new Promise((resolve, reject) => {
        if (!worker.current) { reject(new Error("Обчислювальний модуль ще не готовий.")); return; }
        const requestId = ++counter.current;
        const timer = setTimeout(() => {
            pending.current.delete(requestId);
            reject(new Error("Обчислення перевищило 30 секунд. Звузьте набір даних і почніть заново."));
        }, 30000);
        pending.current.set(requestId, { resolve: value => resolve(value as T), reject, timer });
        worker.current.postMessage({ ...command, requestId });
    }), []);

    return { ready, error, call };
}
